import { useCallback, useEffect, useRef, useState, type FocusEvent } from "react";
import classNames from "classnames";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { EditorState, LexicalEditor } from "lexical";
import { MentionNode } from "@/lib/lexical/nodes/MentionNode";
import MentionPlugin from "@/lib/lexical/plugins/MentionPlugin";
import useMentionSearch from "@/hooks/useMentionSearch";
import { serializeWithMentions, initEditorWithMentions } from "./mentionHelpers";

interface MentionableTextFieldProps {
  value?: string;
  onChange?: (serialized: string) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  placeholder?: string;
  label?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  "data-testid"?: string;
}

function SyncValuePlugin({
  value,
  lastEmittedRef,
  onPopulated
}: {
  value: string;
  lastEmittedRef: { current: string };
  onPopulated: (populated: boolean) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      lastEmittedRef.current = value;
      if (value) {
        initEditorWithMentions(editor, value);
        onPopulated(true);
      }
      return;
    }
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const rootEl = editor.getRootElement();
    const hadFocus = !!rootEl && rootEl === document.activeElement;
    initEditorWithMentions(editor, value);
    onPopulated(value.trim().length > 0);
    if (!hadFocus && rootEl) {
      queueMicrotask(() => {
        if (rootEl === document.activeElement) rootEl.blur();
      });
    }
  }, [editor, value, lastEmittedRef, onPopulated]);

  return null;
}

function EditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);
  return null;
}

const mentionTheme = {
  paragraph: "m-0"
};

export default function MentionableTextField({
  value = "",
  onChange,
  onFocus,
  onBlur,
  placeholder = "Add a comment...",
  label,
  rows = 1,
  className,
  disabled = false,
  autoFocus = false,
  "data-testid": testId = "mentionable-text-field"
}: MentionableTextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isPopulated, setIsPopulated] = useState(!!value);
  const [mentionQuery, setMentionQuery] = useState("");
  const { users, isLoading } = useMentionSearch(mentionQuery);
  const lastEmittedRef = useRef<string>(value);

  const labelFloats = isFocused || isPopulated;

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleChange = useCallback(
    (editorState: EditorState, _editor: LexicalEditor) => {
      const serialized = serializeWithMentions(editorState);
      lastEmittedRef.current = serialized;
      setIsPopulated(serialized.trim().length > 0);
      onChange?.(serialized);
    },
    [onChange]
  );

  const minHeight = Math.max(rows, 1) * 24;

  const editorConfig = {
    namespace: "MentionableTextField",
    theme: mentionTheme,
    editable: !disabled,
    onError: (error: Error) => {
      console.error("[MentionableTextField]", error);
    },
    nodes: [MentionNode]
  };

  return (
    <div className={classNames("relative", className)} data-testid={testId}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className={classNames("relative rounded transition-colors", disabled && "opacity-50 pointer-events-none")}>
          <div
            className={classNames(
              "pointer-events-none absolute inset-0 rounded transition-colors",
              isFocused ? "border-2 border-sys-brand-primary" : "border border-sys-brand-outline"
            )}
            aria-hidden="true"
          />

          {label && (
            <span
              className={classNames(
                "pointer-events-none absolute left-3 z-[2] transition-all duration-150",
                labelFloats
                  ? "text-body-small -top-2 leading-4 bg-sys-brand-surface px-1"
                  : "text-body-large top-4 leading-6 px-1",
                isFocused
                  ? "text-sys-brand-primary"
                  : disabled
                    ? "text-sys-brand-on-surface/38"
                    : "text-sys-brand-on-surface-variant"
              )}
            >
              {label}
            </span>
          )}

          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className="relative z-[1] px-4 py-4 text-body-large text-sys-brand-on-surface outline-none overflow-y-auto"
                style={{ minHeight: `${minHeight}px` }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                data-testid={`${testId}-input`}
              />
            }
            placeholder={
              <div
                className={classNames(
                  "absolute top-4 left-4 text-body-large pointer-events-none z-[1] transition-opacity",
                  labelFloats ? "text-sys-brand-on-surface-variant opacity-100" : "opacity-0"
                )}
              >
                {!label || labelFloats ? placeholder : ""}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <MentionPlugin users={users} isLoading={isLoading} onQueryChange={setMentionQuery} />
        </div>
        <EditablePlugin editable={!disabled} />
        <SyncValuePlugin value={value} lastEmittedRef={lastEmittedRef} onPopulated={setIsPopulated} />
        {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} />
      </LexicalComposer>
    </div>
  );
}
